/**
 This component is used to preview media embed such as videos and audios by providing the url.
 It uses ReactPlayer to play videos and audios.
 */

import './MediaPlayer.scss';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { SlSkeleton } from '@shoelace-style/shoelace/dist/react';
import { MediaResourceTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import ReactPlayer from 'react-player';

import { SmallButton } from '~shared/components/buttons';
import { AudioErrorIcon } from '~shared/components/MediaPlayer/icons/AudioErrorIcon';
import { VideoErrorIcon } from '~shared/components/MediaPlayer/icons/VideoErrorIcon';

import platform from '../../../../../../shared/Platform';

type MediaPreviewerProps = {
  url: string;
  start?: number;
  end?: number;
  type: MediaResourceTypes;
  controls?: boolean;
  light?: boolean;
  containerStyle?: string;
  playerStyle?: string;
  ErrorScreen?: React.ComponentType<{
    onRetry: () => void;
  }>;
};

// Error message/code and a boolean value to determine whether to display the error message or not.
const ignoreErrorsMap = new Map<string, boolean>([
  // The Vimeo player intermittently throws the following error, but the video plays successfully.
  ['PlaybackError', platform.isAndroidApp],
  ["Cannot read properties of undefined (reading 'indexOf')", false],
  /* Error codes for YouTube: Video playback disabled by the owner.
   Reference: https://developers.google.com/youtube/iframe_api_reference#:~:text=onError,error%20in%20disguise */
  ['101', true],
  ['150', true],
]);

export const MediaPlayer = forwardRef(function MediaPlayer(
  {
    url,
    start,
    end,
    type,
    controls = true,
    light = false,
    containerStyle = '',
    playerStyle = '',
    ErrorScreen,
  }: MediaPreviewerProps,
  ref: React.Ref<ReactPlayer>
) {
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);

  useImperativeHandle(ref, () => reactPlayerRef.current);

  const handleError = (error: Error) => {
    const errorMessage = typeof error === 'string' ? error : error.message;

    if (ignoreErrorsMap.has(errorMessage)) {
      setHasError(ignoreErrorsMap.get(errorMessage) ?? false);
      return;
    }
    setHasError(true);
  };

  const handleOnRetry = () => {
    setHasError(false);
  };

  // Reset error screen when url changes
  useEffect(() => {
    return () => {
      setHasError(false);
    };
  }, [url]);

  const mediaDivStyle = cx(
    {
      'flex justify-center items-center': hasError,
    },
    'rounded-2 shadow-outline-px inset-0 text-grey-400'
  );

  const playerDivStyle = cx(
    'rounded-2 shadow-outline-px inset-0 text-grey-400 overflow-hidden',
    playerStyle
  );

  return (
    <div
      className={`${mediaDivStyle} ${containerStyle}`}
      data-testid="media-player"
    >
      {hasError ? (
        ErrorScreen ? (
          <ErrorScreen onRetry={handleOnRetry} />
        ) : (
          <MediaErrorScreen
            onRetry={handleOnRetry}
            errorIcon={
              type === MediaResourceTypes.Video ? (
                <VideoErrorIcon />
              ) : (
                <AudioErrorIcon />
              )
            }
          />
        )
      ) : (
        <ReactPlayer
          data-testid="react-player"
          className={playerDivStyle}
          ref={reactPlayerRef}
          playing={isPlaying}
          url={url}
          controls={controls}
          light={light}
          fallback={<MediaSkeleton />}
          width="100%"
          height="100%"
          onDuration={(duration) => {
            if (!duration || duration < 1) {
              setHasError(true);
              return;
            }

            setHasError(false);
          }}
          playsinline={platform.isWrapperApp()}
          onPlay={() => setIsPlaying(true)}
          onProgress={({ playedSeconds }) => {
            if (!isPlaying) return;

            if (playedSeconds > end) {
              setIsPlaying(false);
              reactPlayerRef.current?.seekTo(end);
            }

            if (playedSeconds < start) {
              setIsPlaying(true);
              reactPlayerRef.current?.seekTo(start);
            }
          }}
          config={{
            youtube: { playerVars: { start, end } },
            vimeo: {
              playerOptions: { minValue: start, maxValue: end, dnt: true },
            },
          }}
          onError={handleError}
        />
      )}
    </div>
  );
});

const MediaSkeleton = () => {
  return (
    <div className="media-skeleton w-full h-full">
      <SlSkeleton className="square w-full h-full" effect="pulse" />
    </div>
  );
};

const MediaErrorScreen = ({
  onRetry,
  errorIcon,
}: {
  onRetry: () => void;
  errorIcon: React.ReactNode;
}) => {
  return (
    <div
      className="flex flex-col justify-center items-center"
      data-testid="media-error-screen"
    >
      {errorIcon}
      <p className="text-base text-blue-900 pb-4">
        {translate('Unexpected Error')}
      </p>
      <SmallButton appearance="ghost" onClick={onRetry}>
        {translate('Retry')}
      </SmallButton>
    </div>
  );
};
